import type { ApolloClient, NormalizedCacheObject } from "@apollo/client";

// Persisted flag key: set when a magic link session starts, cleared only on
// explicit logout. Survives session expiry (when magicLinkSessionToken is
// cleared) so the session-expired modal can still show "Request New Link".
export const MAGIC_LINK_AUTH_TYPE_KEY = "magicLinkAuthType";

// localStorage keys cleared on session expiry AND logout
const AUTH_LOCAL_STORAGE_KEYS = [
  "userId",
  "clientId",
  "csrfToken",
  "local_email",
  "magicLinkSessionToken",
] as const;

// localStorage keys cleared ONLY on explicit logout (not on session expiry)
const LOGOUT_ONLY_LOCAL_STORAGE_KEYS = [MAGIC_LINK_AUTH_TYPE_KEY] as const;

// All sessionStorage keys used for authentication
const AUTH_SESSION_STORAGE_KEYS = ["magicLinkSessionToken", "userId"] as const;

// Non-HttpOnly cookies to clear (HttpOnly cookies like ml_session
// are cleared server-side or expire naturally)
const AUTH_COOKIES = ["userId", "csrf-token"] as const;

/**
 * Detects whether the current user authenticated via magic link.
 *
 * Checks the magicLinkAuthType flag first — this persists through session
 * expiry so the session-expired modal shows "Request New Link" even after
 * magicLinkSessionToken has been cleared.
 * Falls back to the token itself for backwards compatibility.
 */
export function isMagicLinkUser(): boolean {
  return !!(
    localStorage.getItem(MAGIC_LINK_AUTH_TYPE_KEY) ||
    localStorage.getItem("magicLinkSessionToken") ||
    sessionStorage.getItem("magicLinkSessionToken")
  );
}

/**
 * Clears all client-side auth artifacts on session expiry (localStorage,
 * sessionStorage, cookies, Apollo cache). Does NOT clear the
 * magicLinkAuthType flag so the session-expired modal still shows the
 * correct action ("Request New Link" vs "Sign In Again").
 */
export async function clearAuthState(
  apolloClient: ApolloClient<NormalizedCacheObject>,
): Promise<void> {
  // Clear session-scoped keys (token, userId, etc.) but keep authType flag
  for (const key of AUTH_LOCAL_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
  // Clear sessionStorage
  for (const key of AUTH_SESSION_STORAGE_KEYS) {
    sessionStorage.removeItem(key);
  }
  // Clear non-HttpOnly cookies
  for (const cookie of AUTH_COOKIES) {
    document.cookie = `${cookie}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
  }
  // Clear Apollo cache
  try {
    await apolloClient.clearStore();
  } catch (_e) {
    /* ignore — cache may already be empty */
  }
}

/**
 * Full logout: clears everything including the magicLinkAuthType flag.
 * Use this for explicit user-initiated logout, not for session expiry.
 */
export async function clearAuthStateOnLogout(
  apolloClient: ApolloClient<NormalizedCacheObject>,
): Promise<void> {
  await clearAuthState(apolloClient);
  for (const key of LOGOUT_ONLY_LOCAL_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
}
